import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../../context/CRMContext';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/layout/Header';
import { Button, Badge, Table, Modal, StatCard } from '../../components/common/Common';
import { Plus, Search, HandCoins, TrendingUp, CheckCircle, Clock, Edit2, Trash2, FileDown } from 'lucide-react';
import { jsPDF } from 'jspdf';
import './Loans.css';

// Compact Indian currency — auto-scales to the natural unit (Cr / L / K / plain)
const formatCompactINR = (val) => {
  const n = Number(val);
  if (!n) return '0';
  if (n >= 10000000) return (n / 10000000).toFixed(2).replace(/\.?0+$/, '') + ' Cr';
  if (n >= 100000) return (n / 100000).toFixed(2).replace(/\.?0+$/, '') + ' L';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + ' K';
  return n.toLocaleString('en-IN');
};

const Loans = () => {
  const { fetchLoans, fetchLoanStats, deleteLoanRecord } = useCRM();
  const { hasPermission } = useAuth();
  const canAdd = hasPermission('loans.add');
  const canEdit = hasPermission('loans.edit');
  const canDelete = hasPermission('loans.delete');
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadLoans();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLoans = async () => {
    setLoading(true);
    // Bounded fetch: never pull the entire loans table into memory.
    const res = await fetchLoans({ limit: 500, search, status: statusFilter !== 'All' ? statusFilter : null, loan_type: typeFilter !== 'All' ? typeFilter : null });
    if (res.success) setLoans(res.data);
    setLoading(false);
  };

  const loadStats = async () => {
    const res = await fetchLoanStats();
    if (res.success) setStats(res.data);
  };

  // This page stays mounted across navigation (e.g. AddLoan and back), so a
  // plain mount-only fetch shows stale data. Refetch when the tab regains focus.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadLoans();
        loadStats();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredLoans = useMemo(() => {
    return loans.filter(l => {
      const q = search.toLowerCase();
      const matchSearch = !q || l.applicant_name.toLowerCase().includes(q) || l.applicant_phone.includes(q) || (l.loan_reference || '').includes(q);
      const matchStatus = statusFilter === 'All' || l.status === statusFilter;
      const matchType = typeFilter === 'All' || l.loan_type === typeFilter;
      return matchSearch && matchStatus && matchType;
    });
  }, [loans, search, statusFilter, typeFilter]);

  const showNotification = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDeleteLoan = async (id) => {
    if (!window.confirm('Are you sure you want to delete this loan record?')) return;
    const res = await deleteLoanRecord(id);
    if (res.success) {
      showNotification('Loan deleted successfully', 'success');
      loadLoans();
      loadStats();
    } else {
      showNotification(res.message || 'Failed to delete loan', 'error');
    }
  };

  const openViewModal = (loan) => {
    setSelectedLoan(loan);
    setShowViewModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'primary',
      'Approved': 'success',
      'Rejected': 'danger',
      'Active': 'success',
      'Closed': 'gray',
    };
    return colors[status] || 'primary';
  };

  const downloadLoanPdf = (loan) => {
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();   // 595
      const pageH = doc.internal.pageSize.getHeight();  // 842
      const M = 50;
      const contentW = pageW - M * 2;
      const NAVY = [20, 35, 64];
      const GOLD = [176, 141, 48];
      const INK = [33, 37, 41];
      const MUTE = [90, 99, 110];
      const RULE = [196, 204, 214];

      const inr = (v) => (v === null || v === undefined || v === '' ? '—' : 'Rs. ' + Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      const dt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
      const txt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
      const issuedOn = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

      const topY = 56;
      const footerLineY = pageH - 56;

      const drawLetterhead = () => {
        // Wordmark
        doc.setFont('times', 'bold');
        doc.setFontSize(24);
        doc.setTextColor(...NAVY);
        doc.text('Leadboat', M, topY);
        doc.setFont('times', 'italic');
        doc.setFontSize(9.5);
        doc.setTextColor(...MUTE);
        doc.text('Real Estate CRM  |  Loan & Finance Division', M, topY + 15);
        // Right meta
        doc.setFont('times', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...MUTE);
        doc.text('Date of Issue', pageW - M, topY - 8, { align: 'right' });
        doc.setFont('times', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...INK);
        doc.text(issuedOn, pageW - M, topY + 6, { align: 'right' });
        // Double rule
        let ry = topY + 28;
        doc.setDrawColor(...NAVY);
        doc.setLineWidth(1.6);
        doc.line(M, ry, pageW - M, ry);
        doc.setDrawColor(...GOLD);
        doc.setLineWidth(0.6);
        doc.line(M, ry + 3, pageW - M, ry + 3);
        return ry + 3;
      };

      let y = 0;
      const newPage = () => { doc.addPage(); y = drawLetterhead() + 30; };
      const ensure = (h) => { if (y + h > footerLineY - 16) newPage(); };

      y = drawLetterhead() + 34;

      // ── Document title ──
      doc.setFont('times', 'bold');
      doc.setFontSize(17);
      doc.setTextColor(...NAVY);
      doc.text('LOAN STATEMENT', pageW / 2, y, { align: 'center' });
      y += 16;
      doc.setFont('times', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(...MUTE);
      doc.text(
        `Reference No.  ${txt(loan.loan_reference)}        Loan ID  ${txt(loan.loan_id)}`,
        pageW / 2, y, { align: 'center' }
      );
      y += 28;

      // ── Formal label/value table ──
      const formalTable = (caption, rows) => {
        const list = rows.filter(r => r[1] !== undefined && r[1] !== null && r[1] !== '');
        if (!list.length) return;
        ensure(40);
        // Caption
        doc.setFont('times', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...NAVY);
        doc.text(caption, M, y);
        y += 7;
        doc.setDrawColor(...GOLD);
        doc.setLineWidth(0.8);
        doc.line(M, y, M + contentW, y);
        y += 4;

        const labelW = contentW * 0.34;
        const valX = M + labelW;
        const valW = contentW - labelW;
        for (let i = 0; i < list.length; i++) {
          const [label, value] = list[i];
          doc.setFont('times', 'normal');
          doc.setFontSize(10);
          const vLines = doc.splitTextToSize(String(value), valW - 20);
          const rowH = Math.max(24, vLines.length * 12 + 12);
          ensure(rowH);
          // cells
          doc.setFillColor(247, 248, 250);
          doc.rect(M, y, labelW, rowH, 'F');
          doc.setDrawColor(...RULE);
          doc.setLineWidth(0.5);
          doc.rect(M, y, labelW, rowH);
          doc.rect(valX, y, valW, rowH);
          // label
          doc.setFont('times', 'bold');
          doc.setFontSize(9.5);
          doc.setTextColor(...MUTE);
          doc.text(String(label), M + 10, y + rowH / 2 + 3);
          // value
          doc.setFont('times', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(...INK);
          doc.text(vLines, valX + 10, y + (rowH - (vLines.length - 1) * 12) / 2 + 3);
          y += rowH;
        }
        y += 22;
      };

      formalTable('I.  Applicant Particulars', [
        ['Applicant Name', txt(loan.applicant_name)],
        ['Contact Number', txt(loan.applicant_phone)],
        ['Email Address', txt(loan.applicant_email)],
      ]);

      formalTable('II.  Loan Particulars', [
        ['Loan Type', txt(loan.loan_type)],
        ['Sanctioned Amount', inr(loan.loan_amount)],
        ['Rate of Interest', loan.interest_rate ? `${loan.interest_rate}% per annum` : '—'],
        ['Tenure', loan.tenure_months ? `${loan.tenure_months} months` : '—'],
        ['Monthly Instalment (EMI)', inr(loan.emi_amount)],
        ['Current Status', txt(loan.status)],
      ]);

      formalTable('III.  Lender & Schedule', [
        ['Lender / Financier', txt(loan.lender_name)],
        ['Loan Reference No.', txt(loan.loan_reference)],
        ['Date of Sanction / Approval', dt(loan.approval_date)],
        ['Commencement Date', dt(loan.start_date)],
        ['Date of Booking', loan.date_of_booking ? dt(loan.date_of_booking) : ''],
        ['Unit Particulars', loan.unit_details ? txt(loan.unit_details) : ''],
      ]);

      if (loan.notes) {
        ensure(50);
        doc.setFont('times', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...NAVY);
        doc.text('IV.  Remarks', M, y);
        y += 7;
        doc.setDrawColor(...GOLD);
        doc.setLineWidth(0.8);
        doc.line(M, y, M + contentW, y);
        y += 16;
        doc.setFont('times', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...INK);
        const noteLines = doc.splitTextToSize(String(loan.notes), contentW - 8);
        for (let i = 0; i < noteLines.length; i++) {
          ensure(14);
          doc.text(noteLines[i], M, y);
          y += 14;
        }
        y += 14;
      }

      // ── Authorisation block ──
      ensure(86);
      y += 10;
      doc.setFont('times', 'italic');
      doc.setFontSize(8.5);
      doc.setTextColor(...MUTE);
      doc.text(
        'This is a computer-generated statement and does not require a physical signature. The figures stated herein are',
        M, y
      );
      doc.text('indicative and subject to verification against the executed loan agreement.', M, y + 12);
      y += 46;
      doc.setDrawColor(...INK);
      doc.setLineWidth(0.6);
      doc.line(pageW - M - 170, y, pageW - M, y);
      doc.setFont('times', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(...INK);
      doc.text('Authorised Signatory', pageW - M, y + 14, { align: 'right' });
      doc.setFont('times', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...MUTE);
      doc.text('For Leadboat', pageW - M, y + 26, { align: 'right' });

      // ── Footer on every page ──
      const total = doc.internal.getNumberOfPages();
      for (let p = 1; p <= total; p++) {
        doc.setPage(p);
        doc.setDrawColor(...RULE);
        doc.setLineWidth(0.6);
        doc.line(M, footerLineY, pageW - M, footerLineY);
        doc.setFont('times', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...MUTE);
        doc.text('Leadboat  ·  Strictly Private & Confidential', M, footerLineY + 16);
        doc.text(`Issued ${issuedOn}`, pageW / 2, footerLineY + 16, { align: 'center' });
        doc.text(`Page ${p} of ${total}`, pageW - M, footerLineY + 16, { align: 'right' });
      }

      const safeName = String(loan.applicant_name || 'loan').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      doc.save(`Loan_Statement_${safeName}_${loan.loan_id}.pdf`);
    } catch (e) {
      console.error(e);
      showNotification('Failed to generate PDF', 'error');
    }
  };

  const columns = [
    {
      header: 'Applicant', render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <p style={{ fontWeight: 600, color: 'var(--navy-800)', fontSize: '13px' }}>{row.applicant_name}</p>
          <p style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '2px' }}>{row.applicant_phone}</p>
        </div>
      )
    },
    { header: 'Type', render: (row) => <Badge variant="primary">{row.loan_type}</Badge> },
    { header: 'Amount', render: (row) => <span style={{ fontWeight: 600, color: 'var(--gold-700)', fontSize: '13px' }}>₹ {Number(row.loan_amount).toLocaleString('en-IN')}</span> },
    { header: 'Lender', render: (row) => <span style={{ fontSize: '13px', color: 'var(--gray-700)' }}>{row.lender_name || '—'}</span> },
    { header: 'EMI', render: (row) => <span style={{ fontSize: '13px', color: 'var(--gray-700)' }}>₹ {row.emi_amount ? Number(row.emi_amount).toLocaleString('en-IN') : '—'}</span> },
    { header: 'Status', render: (row) => <Badge variant={getStatusColor(row.status)}>{row.status}</Badge> },
    {
      header: 'Actions', width: '130px', render: (row) => (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button className="btn btn--ghost btn--sm" title="Download PDF" onClick={(e) => { e.stopPropagation(); downloadLoanPdf(row); }}><FileDown size={14} /></button>
          {canEdit && <button className="btn btn--ghost btn--sm" title="Edit" onClick={(e) => { e.stopPropagation(); navigate(`/loans/edit/${row.loan_id}`); }}><Edit2 size={14} /></button>}
          {canDelete && <button className="btn btn--ghost btn--sm" title="Delete" onClick={(e) => { e.stopPropagation(); handleDeleteLoan(row.loan_id); }} style={{ color: 'var(--danger)' }}><Trash2 size={14} /></button>}
        </div>
      )
    }
  ];

  return (
    <div>
      <Header
        title="Loan Management"
        subtitle={`${loans.length} loans`}
        actions={canAdd ? <Button variant="gold" icon={Plus} onClick={() => navigate('/loans/add')}>Add Loan</Button> : null}
      />
      <div className="page">
        <div className="page__grid page__grid--stats loans-stats">
          <StatCard icon={HandCoins} label="Total Loans" value={stats.total || 0} />
          <StatCard icon={TrendingUp} label="Total Amount" value={`₹ ${formatCompactINR(stats.total_amount)}`} accent />
          <StatCard icon={CheckCircle} label="Approved" value={stats.by_status?.find(s => s.status === 'Approved')?.count || 0} />
          <StatCard icon={Clock} label="Pending" value={stats.by_status?.find(s => s.status === 'Pending')?.count || 0} />
        </div>

        <div className="filter-bar">
          <div className="filter-bar__search">
            <Search size={16} />
            <input placeholder="Search by name, phone, or ref..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {['All', 'Pending', 'Approved', 'Rejected', 'Active', 'Closed'].map(s => (
            <button key={s} className={`filter-chip ${statusFilter === s ? 'filter-chip--active' : ''}`} onClick={() => setStatusFilter(s)}>{s}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>Loading...</div>
        ) : filteredLoans.length > 0 ? (
          <div className="loans-table">
            <Table columns={columns} data={filteredLoans} onRowClick={openViewModal} />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>No loans found</div>
        )}
      </div>

      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="Loan Details" size="md">
        {selectedLoan && (
          <div>
            <div className="lead-detail__header">
              <div style={{ flex: 1 }}>
                <h3 className="lead-detail__name">{selectedLoan.applicant_name}</h3>
                <p className="lead-detail__sub">{selectedLoan.loan_type}{selectedLoan.loan_reference ? ` · ${selectedLoan.loan_reference}` : ''}</p>
              </div>
              <div>
                <Button variant="primary" icon={Edit2} size="sm" onClick={() => navigate(`/loans/edit/${selectedLoan.loan_id}`)}>Edit</Button>
              </div>
            </div>
            <div className="lead-detail__grid">
              <div className="lead-detail__item"><span className="lead-detail__label">Phone</span><span className="lead-detail__value">{selectedLoan.applicant_phone}</span></div>
              <div className="lead-detail__item"><span className="lead-detail__label">Email</span><span className="lead-detail__value">{selectedLoan.applicant_email || '—'}</span></div>
              <div className="lead-detail__item"><span className="lead-detail__label">Loan Amount</span><span className="lead-detail__value">₹ {Number(selectedLoan.loan_amount).toLocaleString('en-IN')}</span></div>
              <div className="lead-detail__item"><span className="lead-detail__label">EMI</span><span className="lead-detail__value">₹ {selectedLoan.emi_amount ? Number(selectedLoan.emi_amount).toLocaleString('en-IN') : '—'}</span></div>
              <div className="lead-detail__item"><span className="lead-detail__label">Interest Rate</span><span className="lead-detail__value">{selectedLoan.interest_rate ? `${selectedLoan.interest_rate}%` : '—'}</span></div>
              <div className="lead-detail__item"><span className="lead-detail__label">Tenure</span><span className="lead-detail__value">{selectedLoan.tenure_months ? `${selectedLoan.tenure_months} months` : '—'}</span></div>
              <div className="lead-detail__item"><span className="lead-detail__label">Lender</span><span className="lead-detail__value">{selectedLoan.lender_name || '—'}</span></div>
              <div className="lead-detail__item"><span className="lead-detail__label">Status</span><Badge variant={getStatusColor(selectedLoan.status)}>{selectedLoan.status}</Badge></div>
              <div className="lead-detail__item"><span className="lead-detail__label">Approval Date</span><span className="lead-detail__value">{selectedLoan.approval_date ? String(selectedLoan.approval_date).slice(0, 10) : '—'}</span></div>
              <div className="lead-detail__item"><span className="lead-detail__label">Start Date</span><span className="lead-detail__value">{selectedLoan.start_date ? String(selectedLoan.start_date).slice(0, 10) : '—'}</span></div>
            </div>
            {selectedLoan.notes && (
              <div style={{ marginTop: '20px', padding: '12px', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
                <p style={{ fontSize: '12px', color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>{selectedLoan.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {toast?.message && (
        <div className={`toast toast--${toast.type}`} style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          padding: '12px 16px',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 500,
          zIndex: 9999,
          backgroundColor: toast.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default Loans;
